// ABOUTME: The phone's escape hatch from a portrait film it cannot decode: recognises the failure on a
// ABOUTME: <video> surface, and latches the desktop film pair for the rest of the tab session.

import { mediaUrl } from './reelSchema';

// Why this exists (launch, 2026-09-19): the portrait `_mobile.mp4` pair was re-exported as H.264
// level 6.0, which iPhone and iPad refuse while every other browser plays it. The player had no way
// back to the desktop pair, so the guest got a black film under a talking agent. The asset gate now
// lives in the backend (`scripts/check_reel_media.py`); this is the runtime floor for whatever an
// asset gate cannot see.

const LATCH_KEY = 'versai_reel_mobile_media_failed';

// MEDIA_ERR_DECODE (3) and MEDIA_ERR_SRC_NOT_SUPPORTED (4) — the file cannot be played here. Not
// MEDIA_ERR_ABORTED (1), which is a routine src swap, nor MEDIA_ERR_NETWORK (2), a connection
// problem the desktop pair would not fix. Code 4 is not proof on its own: the HTML media algorithm
// also reports it when the fetch dies before metadata (the "dedicated media source failure" steps),
// which on a phone with patchy data is a network problem, not an unplayable file.
const UNPLAYABLE_ERROR_CODES = new Set([3, 4]);
const NEEDS_PROOF_ERROR_CODE = 4;

// Media time a surface must have advanced before "no picture" is trusted. A file with a video track
// always reports its dimensions once it has metadata, so a surface whose clock is running with a
// 0x0 picture is playing the audio of a video it cannot decode. Below this, a slow start is not
// distinguishable from a failure, so it is never judged.
const MIN_PLAYED_S = 1;

let latched = null; // null = not read from sessionStorage yet

/** True once this tab has seen the portrait pair fail — every later load uses the desktop pair. */
export function mobileMediaFailed() {
  if (latched === null) {
    try {
      latched = window.sessionStorage.getItem(LATCH_KEY) === '1';
    } catch {
      latched = false;
    }
  }
  return latched;
}

/**
 * Records the failure. Session-scoped on purpose: a reload in this tab must not retry the file that
 * just failed, but a new visit should, because the asset may have been fixed since.
 */
export function latchMobileMediaFailure() {
  latched = true;
  try {
    window.sessionStorage.setItem(LATCH_KEY, '1');
  } catch {
    // Private mode / storage disabled: the in-memory latch still holds for this page.
  }
}

export function __resetMobileMediaFallbackForTests() {
  latched = null;
  try {
    window.sessionStorage.removeItem(LATCH_KEY);
  } catch {
    // ditto
  }
}

const mediaUrlCache = new WeakMap();
function manifestMediaUrls(manifest) {
  let urls = mediaUrlCache.get(manifest);
  if (!urls) {
    urls = new Set(manifest.chapters.map((ch) => mediaUrl(manifest, ch)));
    mediaUrlCache.set(manifest, urls);
  }
  return urls;
}

/**
 * A candidate failure of the portrait pair on this surface, or null.
 *
 * Only a manifest built from the phone's own source (`mobileMedia`, set by useChapterManifest) is
 * ever judged, and only a surface currently holding one of that manifest's films — so a desktop
 * composition, an emptied surface, and a src swap in flight all read as null. Every surface counts,
 * not only the active one: the boot film loads on a surface that is not active yet, which is
 * exactly where the launch failure happened.
 *
 * @param {object|null} manifest
 * @param {HTMLVideoElement|null} el
 * @param {'error'|'progress'} kind — `error` from the element's error event, `progress` from timeupdate
 * @returns {{reason: string, src: string, proven: boolean}|null} `proven` is false only for a
 *   MediaError 4, which proveMobileMediaFailure must confirm first.
 */
export function mobileMediaFailure(manifest, el, kind) {
  if (!manifest?.mobileMedia || !el) return null;
  const src = el.getAttribute('src') || '';
  if (!src || !manifestMediaUrls(manifest).has(src)) return null;
  if (kind === 'error') {
    const code = el.error?.code;
    if (!UNPLAYABLE_ERROR_CODES.has(code)) return null;
    return { reason: `MediaError ${code} on ${src}`, src, proven: code !== NEEDS_PROOF_ERROR_CODE };
  }
  if (kind === 'progress') {
    const blind = el.currentTime >= MIN_PLAYED_S && !el.videoWidth && !el.videoHeight;
    return blind ? { reason: `no picture after ${el.currentTime.toFixed(1)}s of ${src}`, src, proven: true } : null;
  }
  return null;
}

// How long the reachability probe may take. The player's own handling of the error waits on it, so a
// flaky connection (the case the probe exists for) must not hold a boot film black for the browser's
// full network timeout; past this, the network is the likelier cause.
export const REACHABILITY_TIMEOUT_MS = 4000;

/**
 * Whether the URL answers right now. `no-cors` because only reachability matters (an opaque
 * response still proves the network got there, and a 404 means the file really is not playable),
 * and because the film host's CORS rules are not this check's business. `no-store` so a cached
 * answer cannot hide a dead connection. A probe that outlives the timeout reads as unreachable.
 */
export function urlIsReachable(url) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REACHABILITY_TIMEOUT_MS);
  return fetch(url, { method: 'HEAD', mode: 'no-cors', cache: 'no-store', signal: controller.signal })
    .then(() => true, () => false)
    .finally(() => clearTimeout(timer));
}

/** Resolves the failure's reason once it is proven, or null when the network is the likelier cause. */
export async function proveMobileMediaFailure(failure, isReachable = urlIsReachable) {
  if (!failure) return null;
  if (failure.proven) return failure.reason;
  return (await isReachable(failure.src)) ? `${failure.reason} (the host answers)` : null;
}

/**
 * Routes one surface `error`: a proven portrait-pair failure to `swap`, everything else to the
 * player's own handling (`fallThrough`). Synchronous whenever no portrait film is involved, so a
 * desktop error reaches the player exactly as before; only a phone's candidate failure waits on proof.
 *
 * @returns {Promise<boolean>} whether it swapped
 */
export function routeSurfaceError(manifest, el, { swap, fallThrough, isReachable = urlIsReachable }) {
  const failure = mobileMediaFailure(manifest, el, 'error');
  if (!failure) {
    fallThrough();
    return Promise.resolve(false);
  }
  return proveMobileMediaFailure(failure, isReachable).then((reason) => {
    if (reason && swap?.(reason)) return true;
    // Not proven, or refused: the player's own handling — but only while this surface still shows
    // that film on a live stage. Another surface may have swapped the session meanwhile, and the
    // remounted-away player must not end a boot film (and send intro_finished) under the new one.
    if (el.isConnected && el.getAttribute('src') === failure.src) fallThrough();
    return false;
  });
}

/** A surface's `timeupdate`: a running clock with no picture is proof enough on its own. */
export function routeSurfaceProgress(manifest, el, swap) {
  const failure = mobileMediaFailure(manifest, el, 'progress');
  if (failure) swap?.(failure.reason);
}
